import { useQuery } from '@tanstack/react-query';
import getMetricsData from '@api/metricContainer';
import { get } from 'lodash';
import instance from '@api/axiosInstance';
import {
  getFirstAppliedFilter,
  intervalSelector,
  realTimeModeOnTrendsSelector,
  supportedMetricsSelector,
} from '@store/common/selectors';
import { useSelector } from 'react-redux';
import { DATASET, isAppModule } from '@utils/constants';
import { SESSIONS_METRICS } from '../widgets/constants';
import { activeDashboardSelector } from '@src/store/tempDashboard/selectors';
import { getInvertedPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';

export const createQuery = (
  metricList: string[] = [],
  interval,
  filter = [],
  realTimeOnTrends = false,
  options = {},
) => {
  const query = {
    type: 'timeseries',
    dataset: DATASET,
    granularity: 'ALL',
    options: {
      withTotals: true,
      ...(options || {}),
    },
    interval,
    filter,
    metrics: metricList,
    isLiveMode: realTimeOnTrends,
    context: window.conviva.context,
  };

  if (realTimeOnTrends) {
    query.ignoreInterval = true;
  }

  return {
    queries: [query],
  };
};

function useFetchSummaryPanelData(metricList: string[] = [], currentKpi) {
  const interval = useSelector(intervalSelector);
  const realTimeOnTrends: boolean = useSelector(realTimeModeOnTrendsSelector);
  const { rule: filter, _rKey = 0 } = useSelector(getFirstAppliedFilter);
  const allowedMetrics: any = useSelector(supportedMetricsSelector);
  const { dataScope, usecaseKey }: any = useSelector(activeDashboardSelector);

  const allowedMetricList = metricList.filter(
    (metric) =>
      metric in allowedMetrics ||
      getInvertedPercentageMetricByKey({ metricKey: metric }) in allowedMetrics,
  );
  const allowedWidgetMetricsList = [...new Set([...SESSIONS_METRICS, ...allowedMetricList])]; // should remove session metrics when spi or sessions widget is not there

  const options = isAppModule
    ? {
        dataScope,
        presetKey: usecaseKey,
        kpiConfig: currentKpi,
      }
    : {
        kpiConfig: currentKpi,
      };
  const payload = createQuery(
    allowedWidgetMetricsList,
    `${interval.from}/${interval.to}`,
    filter,
    realTimeOnTrends,
    options,
  );

  return useQuery(
    ['summary-panel', payload.queries, _rKey, currentKpi],
    () => getMetricsData(payload, {}, instance),
    {
      retry: false,
      refetchOnWindowFocus: false,
      enabled: !!metricList.length,
      keepPreviousData: realTimeOnTrends,
      staleTime: 5 * 60 * 1000, // 5m
      select: (response) => {
        const { data, totals } = get(response, ['data', '0'], []);
        return { totals, data };
      },
    },
  );
}

export default useFetchSummaryPanelData;
